import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../api';
import { getRoles, getRolePermissions } from '../pages/SetPermission/api/roleApi';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [capabilities, setCapabilities] = useState({});
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);

  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch (error) {
      return true;
    }
  };

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          
          // Check if token is still valid
          if (userData.token && !isTokenExpired(userData.token)) {
            setUser(userData);
            // Set token in axios headers
            api.defaults.headers.common['Authorization'] = `Bearer ${userData.token}`;
            
            // Fetch fresh profile data to sync jobTitle and other fields
            try {
              const profileRes = await api.get('/crm/profile');
              if (profileRes?.data) {
                // Normalize roles: convert from objects to strings for compatibility
                let normalizedRoles = userData.roles; // Keep original by default
                if (profileRes.data.roles && Array.isArray(profileRes.data.roles)) {
                  normalizedRoles = profileRes.data.roles.map(role => {
                    // If role is an object with 'name' property, extract the name
                    if (typeof role === 'object' && role.name) {
                      return role.name;
                    }
                    // If role is already a string, keep it
                    return role;
                  });
                }
                
                // Merge profile data with stored user data (keep token from stored user)
                const updatedUser = {
                  ...userData,
                  ...profileRes.data,
                  roles: normalizedRoles, // Use normalized roles (array of strings)
                  token: userData.token, // Keep original token
                  refreshToken: userData.refreshToken, // Keep original refreshToken
                };
                
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
                
                // Fetch capabilities after setting user
                await fetchCapabilities(updatedUser);
              }
            } catch (profileError) {
              // Continue with stored user data if profile fetch fails
              // Still try to fetch capabilities with stored user data
              await fetchCapabilities(userData);
            }
          } else {
            // Token expired, clear storage
            localStorage.removeItem('user');
            setUser(null);
            delete api.defaults.headers.common['Authorization'];
          }
        }
      } catch (error) {
        localStorage.removeItem('user');
        setUser(null);
        delete api.defaults.headers.common['Authorization'];
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Auto logout when token expires
  useEffect(() => {
    if (!user) return;

    const checkTokenExpiration = () => {
      if (isTokenExpired(user.token)) {
        logout();
      }
    };

    // Check every minute
    const interval = setInterval(checkTokenExpiration, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const login = async (userData) => {
    try {
      // Validate userData structure
      if (!userData || !userData.token) {
        throw new Error('Invalid user data structure');
      }
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      // Set token in axios headers
      api.defaults.headers.common['Authorization'] = `Bearer ${userData.token}`;
      
      // Fetch capabilities after login
      await fetchCapabilities(userData);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    try {
      setUser(null);
      setCapabilities({});
      localStorage.removeItem('user');
      // Clear token from axios headers
      delete api.defaults.headers.common['Authorization'];
    } catch (error) {
      // Silent error handling
    }
  };

  const updateUser = (updates) => {
    try {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      // Silent error handling
    }
  };

  const isAuthenticated = () => {
    try {
      const authenticated = user && user.token && !isTokenExpired(user.token);
      return authenticated;
    } catch (error) {
      return false;
    }
  };

  const hasRole = (role) => {
    try {
      if (!user || !user.roles || !Array.isArray(user.roles)) {
        return false;
      }
      
      // Check if roles array contains the role (handle both string and object formats)
      const hasRoleResult = user.roles.some(r => {
        // If r is a string, compare directly
        if (typeof r === 'string') {
          return r === role;
        }
        // If r is an object with 'name' property, compare the name
        if (typeof r === 'object' && r.name) {
          return r.name === role;
        }
        return false;
      });
      
      return hasRoleResult;
    } catch (error) {
      return false;
    }
  };

  // Fetch user capabilities from role permissions API
  const fetchCapabilities = async (userData) => {
    try {
      setCapabilitiesLoading(true);
      
      // Get all roles mapping (id <-> name)
      const allRoles = await getRoles().catch(() => []);
      
      // Normalize role name variants from auth payload
      const userRoleNames = Array.isArray(userData?.roles) 
        ? userData.roles.map(r => typeof r === 'object' ? r.name : r)
        : [];
      
      // Map user roles (e.g., 'ROLE_ADMIN' or 'ADMIN') to role ids
      const myRoleIds = allRoles
        .filter(r => {
          const nameUpper = (r.name || r.nameRole || '').toString().toUpperCase();
          return userRoleNames.some(ur => {
            const urUpper = ur.toString().toUpperCase();
            return urUpper === `ROLE_${nameUpper}` || urUpper === nameUpper;
          });
        })
        .map(r => r.id);

      // Fetch permissions for each role and merge (any true wins)
      const permsList = await Promise.all(
        (myRoleIds || []).map(rid => getRolePermissions(rid).catch(() => ({})))
      );
      
      const merged = {};
      for (const rp of permsList) {
        Object.entries(rp || {}).forEach(([moduleKey, caps]) => {
          merged[moduleKey] = merged[moduleKey] || {};
          Object.entries(caps || {}).forEach(([capKey, val]) => {
            merged[moduleKey][capKey] = Boolean(merged[moduleKey][capKey]) || Boolean(val);
          });
        });
      }

      setCapabilities(merged);
      return merged;
    } catch (e) {
      console.error('Error loading capabilities:', e);
      setCapabilities({});
      return {};
    } finally {
      setCapabilitiesLoading(false);
    }
  };

  // Check if user has a specific capability in a module
  const can = (capabilityKey, moduleKey = 'proposal') => {
    try {
      if (hasRole('ROLE_ADMIN')) return true;
      const mod = capabilities[moduleKey] || {};
      return !!mod[capabilityKey];
    } catch (error) {
      return false;
    }
  };

  const hasPermission = (permission) => {
    try {
      if (!user) return false;
      
      // Normalize permission format: "can approve" -> "can_approve", "can accept" -> "can_accept"
      const normalizePermission = (perm) => {
        // Convert "can approve" to "can_approve", "can accept" to "can_accept"
        if (perm === 'can approve') return 'can_approve';
        if (perm === 'can accept') return 'can_accept';
        return perm;
      };
      
      const normalizedPermission = normalizePermission(permission);
      
      // Check module-based capabilities first
      if (Object.keys(capabilities).length > 0) {
        // Check in proposal module by default
        if (can(normalizedPermission, 'proposal') || can(permission, 'proposal')) {
          return true;
        }
      }
      
      // Check if user has permissions array (from JWT)
      if (user.permissions && Array.isArray(user.permissions)) {
        return user.permissions.some(p => {
          // If p is a string, compare directly
          if (typeof p === 'string') {
            return p === normalizedPermission || p === permission;
          }
          // If p is an object with 'name' or 'permission' property
          if (typeof p === 'object') {
            return p.name === normalizedPermission || p.permission === normalizedPermission ||
                   p.name === permission || p.permission === permission;
          }
          return false;
        });
      }
      
      // Check if user has capabilities array (from JWT)
      if (user.capabilities && Array.isArray(user.capabilities)) {
        return user.capabilities.some(c => {
          if (typeof c === 'string') {
            return c === normalizedPermission || c === permission;
          }
          if (typeof c === 'object') {
            return c.name === normalizedPermission || c.capability === normalizedPermission ||
                   c.name === permission || c.capability === permission;
          }
          return false;
        });
      }
      
      // Fallback: Map specific permissions to roles for backward compatibility
      // This allows gradual migration to permission-based system
      if (permission === 'can approve' || normalizedPermission === 'can_approve') {
        return hasRole('ROLE_MANAGER') || hasRole('ROLE_DIRECTOR');
      }
      if (permission === 'can accept' || normalizedPermission === 'can_accept') {
        return hasRole('ROLE_SALES');
      }
      
      return false;
    } catch (error) {
      return false;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated,
    hasRole,
    hasPermission,
    can,
    capabilities,
    capabilitiesLoading,
    fetchCapabilities
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
